import type { ChartPoint } from './HistoryChart';

// Local (sliding-window) Hampel outlier filter for noisy measured series
// (sensor spikes, flow-temp glitches, open-circuit sentinels). For each point
// it compares the value to the MEDIAN of a small window of neighbours and the
// local MAD (median absolute deviation), dropping points that deviate by more
// than K · 1.4826 · MAD.
//
// Why local, not global: a global median/MAD treats a sustained but legitimate
// level change (e.g. a warm afternoon sitting steady ~1.5 °C above the rest of
// the day) as one giant outlier block and hides it — and on very steady data
// the global MAD collapses toward 0, making the threshold absurdly tight. A
// local window tracks the trend, so only genuinely isolated spikes go.
//
// An absolute MAD floor stops the threshold collapsing on steady data (where
// MAD → ~0), which would otherwise clip normal sub-degree fluctuation. A
// physical sanity bound drops impossible values (e.g. −273.15 open-circuit
// sentinels) regardless of their neighbourhood.

const HALF_WINDOW = 7;          // ±7 samples (~15-wide window)
const K = 3;                    // Hampel sensitivity (standard 3 sigma)
const MAD_TO_SIGMA = 1.4826;    // MAD → std-dev estimate for normal data
const MAD_FLOOR_C = 0.15;       // min spread so steady data isn't over-filtered
const MIN_POINTS = 7;           // need a window to reason about local spread
const PHYS_MIN_C = -40;         // below this is a sensor sentinel, never real
const PHYS_MAX_C = 90;          // above this is non-physical for our temps

function median(values: number[]): number {
  const sorted = [...values].sort((a, b) => a - b);
  const n = sorted.length;
  const mid = Math.floor(n / 2);
  return n % 2 ? sorted[mid]! : (sorted[mid - 1]! + sorted[mid]!) / 2;
}

// Hard prefilter: drop non-finite and physically-impossible samples. Split out
// of madFilter so callers that must NOT run the local Hampel test — e.g.
// server-bucketed series, where the points are already aggregated — can still
// reject sentinels. A sustained open-circuit fault survives the backend's
// per-bucket median as a −273.15 reading; plotting it blows the y-domain open
// and flattens the real data into a line.
export function sanitize(points: ChartPoint[]): ChartPoint[] {
  return points.filter(
    (p) => Number.isFinite(p.v) && p.v >= PHYS_MIN_C && p.v <= PHYS_MAX_C,
  );
}

export function madFilter(points: ChartPoint[]): ChartPoint[] {
  // 1. Drop non-finite and physically-impossible samples up front, so a run of
  //    sentinel values can't poison the local windows below.
  const clean = sanitize(points);
  if (clean.length < MIN_POINTS) return clean;

  const v = clean.map((p) => p.v);

  // 2. Local Hampel test: each point against the median/MAD of its neighbours.
  return clean.filter((_p, i) => {
    const lo = Math.max(0, i - HALF_WINDOW);
    const hi = Math.min(v.length - 1, i + HALF_WINDOW);
    const window = v.slice(lo, hi + 1);
    const med = median(window);
    const mad = median(window.map((x) => Math.abs(x - med)));
    const sigma = MAD_TO_SIGMA * Math.max(mad, MAD_FLOOR_C);
    return Math.abs(v[i]! - med) <= K * sigma;
  });
}
